<template>
  <div>
    <template v-if="!loading">
      <div v-if="submitError" class="alert alert-danger" data-submit-error>
        <button
          type="button"
          class="close"
          data-dismiss="alert"
          aria-label="Close"
          @click="submitError = false"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        {{ $t('enrollmentStep.accountCreation.error') }}
        <div v-for="extraError in extraErrors" :key="extraError">
          {{ extraError }}
        </div>
      </div>
      <h1 class="text-center">
        {{ $t('enrollmentStep.accountCreation.enrollIn') }} {{ study.portal_name || study.name }}
      </h1>
      <p v-if="isLoginEnabled" class="text-center">
        <router-link :to="{name: 'login'}">
          {{ $t('enrollmentStep.accountCreation.loginLink') }}
        </router-link>
      </p>

      <template v-if="!enrollmentDisabled">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-if="config.instructional_text" v-html="$sanitize(config.instructional_text)" />

        <fieldset>
          <legend>{{ $t('enrollmentStep.accountCreation.createAccount') }}</legend>
          <form @submit.prevent="submitHandler">
            <ProfileFields
              ref="profileFields"
              v-model="profileFields"
              :config="config.fields"
              :isLoginEnabled="isLoginEnabled"
              @focus="focusProfileFields"
            />

            <div class="row">
              <div class="col-md-12">
                <input
                  :value="ctaText"
                  :class="{ctaSize: true, 'btn-loading': saving}"
                  type="submit"
                  class="btn btn-primary"
                >
              </div>
            </div>
          </form>
        </fieldset>
        <b-modal
          v-if="useConsent"
          id="consent"
          v-model="showConsent"
          class="consent-modal"
          :title="$t('disclosure')"
          size="lg"
        >
          <p v-if="consentFirst" class="lead text-center">
            {{ $t('enrollmentStep.accountCreation.disclosureInstructions') }}
          </p>

          <survey :survey="consentSurvey" />

          <div slot="modal-footer">
            <button
              :class="{'btn-loading': saving}"
              :disabled="!isLastPage"
              type="button"
              class="btn btn-primary btn-block"
              data-button="consent-agree"
              @click="completeConsent"
            >
              {{ $t('enrollmentStep.accountCreation.iAgree') }}
            </button>
          </div>
        </b-modal>
      </template>
      <template v-else>
        <p class="text-center lead mb-4" style="white-space: pre-wrap" v-html="$sanitize(linkify(config.disabled_message))" />
      </template>
    </template>
  </div>
</template>

<script>
  import { mask } from 'vue-the-mask';
  import { BModal } from "bootstrap-vue";

  import * as SurveyJS from 'survey-vue';
  import '@/FormWidgets/surveyjs/surveyVueLocalization';
  import * as widgets from 'surveyjs-widgets';

  import linkifyStr from "linkify-string";
  import ProfileFields from './ProfileFields.vue';

  SurveyJS.Survey.cssType = "bootstrap";
  SurveyJS.defaultBootstrapCss.navigationButton = 'btn btn-default';

  export default {
    components: {
      ProfileFields,
      BModal,
      survey: SurveyJS.Survey
    },
    directives: {
      mask
    },
    props: {
      'studyId': {
        type: Number,
        required: true,
      },
      'initialStudy': {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        loading: true,
        saving: false,
        study: null,
        config: null,
        showConsent: false,
        submitError: false,
        passwordStrength: 0,
        consentSurvey: null,
        consentComplete: false,
        extraErrors: [],
        // profile fields
        profileFields: {
          first_name: '',
          last_name: '',
          email: '',
          username: '',
          password: '',
          date_of_birth: '',
          cell_phone: '',
          home_phone: '',
          street_address: '',
          street_address2: '',
          city: '',
          state_region: '',
          postal_code: '',
          time_zone: '',
          locale: '',
          external_id: '',
          // notification prefs
          notify_email: false,
          notify_text: false,
          notify_voice: false,
        }
      };
    },
    computed: {
      isLoginEnabled() {
        return this.config.loginEnabled;
      },
      enrollmentDisabled() {
        return this.study
            ? (!this.config.enabled || this.study.status === 'Finished')
            : false;
      },
      useConsent() {
        return Boolean(this.config.consent.source_id !== null && this.consentSurvey);
      },
      consentFirst() {
        return this.config.consent.order === 'before';
      },
      ctaText() {
        return this.useConsent && !this.consentFirst ? this.$t('continue') : this.$t('enroll');
      },
      ctaSize() {
        return this.useConsent && !this.consentFirst ? '' : 'btn-block';
      },
      isLastPage() {
        return this.consentSurvey ? this.consentSurvey.isLastPage : false;
      },
      registrationData() {
        // copy profile fields
        const data = Object.assign({}, this.profileFields);

        // remove empty fields
        Object.keys(data).forEach((field) => {
          if (data[field] === '') {
            delete data[field];
          }
        });

        // remove notification pref fields if they're all hidden
        if (!this.config.fields.notify_prefs) {
          delete data['notify_email'];
          delete data['notify_text'];
          delete data['notify_voice'];
        } else {
          // remove boolean fields that are not displayed
          if (!this.config.fields['notify_email']) {
            delete data['notify_email'];
          }
          if (!this.config.fields['notify_text']) {
            delete data['notify_text'];
            delete data['cell_phone'];
          }
          if (!this.config.fields['notify_voice']) {
            delete data['notify_voice'];
            delete data['home_phone'];
          }
        }

        // assign study id
        data['study_id'] = this.studyId;

        // locale
        if (this.$store.state.useLocales) {
          data['locale'] = this.$i18n.locale;
        }

        // consent
        if (this.useConsent) {
          data.consentResponse = this.consentSurvey.data;
        }

        return data;
      }
    },
    created() {
      if (this.initialStudy !== null) {
        this.setConfig(this.initialStudy);
      }

      this.getConfig()
        .then(() => {
          this.loading = false;
          this.$emit('ready');
        });
    },
    methods: {
      getConfig() { // returns promise
        if (this.config !== null) {
          // immediately resolve
          return Promise.resolve();
        }

        return this.$api.Studies.getStudy({
          'study_id': this.studyId
        }).then(({body}) => {
          // TODO: don't blow up if any prop is missing
          this.setConfig(body.data);
        });
      },
      setConfig(study) {
        this.study = study;
        // this is some Tom Boutell level stuff, but JS is a whiny toddler
        this.config = safeNestedProp(study, 'configuration.enrollment.accountCreation');
        this.config.fields = safeNestedProp(study, 'configuration.profile.accountCreation');
        this.config.defaults = safeNestedProp(study, 'configuration.profile.defaults');
        this.config.loginEnabled = safeNestedProp(study, 'configuration.portal.loginEnabled');

        if (this.config.consent.source_id !== null) {
          // hide survey navigation if only one page
          if (this.config.consent.surveySchema.pages.length === 1) {
            this.config.consent.surveySchema.showNavigationButtons = false;
          }
          this.consentSurvey = new SurveyJS.Model(this.config.consent.surveySchema);
          this.consentSurvey.locale = this.$i18n.locale;
        }

        if (this.config.defaults) {
          this.profileFields.notify_email = this.config.defaults.notifications.notify_email || false;
          this.profileFields.notify_text = this.config.defaults.notifications.notify_text || false;
          this.profileFields.notify_voice = this.config.defaults.notifications.notify_voice || false;

          if (this.config.defaults.time_zone && this.config.fields.time_zone) {
            let tzd = this.config.defaults.time_zone;
            if (!isNaN(tzd)) {
              tzd = this.config.defaults.time_zone.toFixed(1);
            }
            this.profileFields.time_zone = tzd || '-5.0';
          }
        }
      },
      submitHandler() {
        this.$refs.profileFields.validate().then((result) => {
            // result = true if no errors
            if (result) {
                if (this.useConsent && !this.consentFirst) {
                  this.showConsent = true;
                } else {
                  this.createAccount();
                }
            }
        });
      },
      focusProfileFields(event) {
        if (this.useConsent && this.consentFirst && !this.consentComplete) {
          if (event && event.target) {
            event.target.blur();
          }
          this.showConsent = true;
        }
      },
      completeConsent() {
        if (!this.consentSurvey.isCurrentPageHasErrors) {
          if (this.consentFirst) {
            this.consentComplete = true;
            this.showConsent = false;
          } else {
            this.createAccount();
          }
        }
      },
      createAccount() {
        this.saving = true;
        this.extraErrors = [];

        this.$api.Authentication.register({participant: this.registrationData})
          .then(({body, status}) => {
            if (status >= 400) {
              throw { status: status, body: body};
            }

            // set account in our store so the rest of the portal knows about it
            this.$store.commit('SET_ACCOUNT', body.data);

            // we have a new account and we're logged in, tell parent to refresh
            this.$emit('refresh');
            this.saving = false;
          })
          .catch((error) => {
            this.submitError = true;
            if (error.response && error.response.body && error.response.body.field_errors) {
              this.$refs.profileFields.pushLocalizedErrors(error.response.body.field_errors);

              error.response.body.field_errors.forEach((field_error) => {
                if (!this.config.fields[field_error.field]) {
                  this.extraErrors.push(field_error.message);
                }
              });
            }
            this.saving = false;
          });
      },
      linkify(message) {
        return linkifyStr(message, { target: { url: null }});
      }
    }
  }

  function safeNestedProp(object, prop) {
    const props = prop.split('.');
    return props.reduce((o, p) => (o && o.hasOwnProperty(p)) ? o[p] : null, object);
  }
</script>

<style>
label > small {
  font-weight: normal;
  font-style: italic;
}

.radio > label > input {
  opacity: 1 !important;
  z-index: auto;
}
</style>
